// admin/src/App.js
import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, doc, updateDoc, getDoc } from 'firebase/firestore';

// Import the new CustomerHistoryPanel component
import CustomerHistoryPanel from './CustomerHistoryPanel.jsx'; // Corrected import path

// Your web app's Firebase configuration for Admin
// IMPORTANT: Replace these with your actual Firebase client-side config from your Firebase project settings
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKING_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

function App() {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [activeTab, setActiveTab] = useState('orders'); // 'orders', 'history' - 'customers' tab removed
    const [orders, setOrders] = useState([]);
    // Removed 'customers' state as the tab is being removed
    const [loading, setLoading] = useState(true);
    // Removed 'searchTerm' state as customer search is no longer directly in App.js
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [refundReason, setRefundReason] = useState(''); // New state for refund reason
    const [message, setMessage] = useState({ type: '', text: '' }); // 'success' or 'error'

    // State for custom confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalContent, setConfirmModalContent] = useState('');
    const [confirmAction, setConfirmAction] = useState(null); // Function to execute on confirmation

    // State for collapsible sections in order details modal
    const [showSenderDetails, setShowSenderDetails] = useState(false);
    const [showReceiverDetails, setShowReceiverDetails] = useState(false);
    const [showPackageDetails, setShowPackageDetails] = useState(false);
    const [showOptionsDetails, setShowOptionsDetails] = useState(false);
    const [showInternationalDetails, setShowInternationalDetails] = useState(false);


    // Firebase instances as state
    const [firebaseAuthInstance, setFirebaseAuthInstance] = useState(null);
    const [firestoreDbInstance, setFirestoreDbInstance] = useState(null);
    const [isFirebaseInitialized, setIsFirebaseInitialized] = useState(false);

    // Initialize Firebase and set up auth listener
    useEffect(() => {
        try {
            // Check if firebaseConfig has necessary keys before initializing
            if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId) {
                console.error("Firebase configuration is incomplete or missing. Please check environment variables.");
                setMessage({ type: 'error', text: 'Firebase configuration is incomplete. Cannot initialize Firebase.' });
                setLoading(false);
                return;
            }

            const app = initializeApp(firebaseConfig);
            const auth = getAuth(app);
            const db = getFirestore(app);

            setFirebaseAuthInstance(auth);
            setFirestoreDbInstance(db);
            setIsFirebaseInitialized(true);

            // Log the Firebase Project ID on initialization
            console.log("[Firebase Init] Using Project ID:", process.env.REACT_APP_FIREBASE_PROJECT_ID);


            const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
                setUser(currentUser);
                setLoading(false); // Stop main loading once auth state is known
                if (currentUser) {
                    fetchOrders();
                    // Removed fetchCustomers as the tab is removed
                } else {
                    // Attempt anonymous sign-in if no user is authenticated
                    try {
                        const initialAuthToken = typeof __initial_auth_token !== 'undefined' ? __initial_auth_token : null;
                        if (initialAuthToken) {
                            await signInWithCustomToken(auth, initialAuthToken);
                        } else {
                            await signInAnonymously(auth);
                        }
                    } catch (error) {
                        console.error("Error during anonymous sign-in:", error);
                        setLoginError(`Failed to authenticate: ${error.message}`);
                    }
                }
            });

            return () => unsubscribe(); // Cleanup auth listener on component unmount
        } catch (error) {
            console.error("Error initializing Firebase:", error);
            setMessage({ type: 'error', text: `Failed to initialize Firebase: ${error.message}` });
            setLoading(false);
        }
    }, []); // Empty dependency array means this runs once on mount

    // Function to show custom confirmation modal
    const showCustomConfirmModal = (content, action) => {
        setConfirmModalContent(content);
        setConfirmAction(() => action); // Store the function to be called
        setShowConfirmModal(true);
    };

    // Function to close custom confirmation modal
    const closeCustomConfirmModal = () => {
        setShowConfirmModal(false);
        setConfirmModalContent('');
        setConfirmAction(null);
    };

    const handleConfirm = () => {
        if (confirmAction) {
            confirmAction(); // Execute the stored action
        }
        closeCustomConfirmModal();
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        try {
            await signInWithEmailAndPassword(firebaseAuthInstance, email, password);
            // User is set by onAuthStateChanged listener
        } catch (error) {
            setLoginError('Failed to log in: ' + error.message);
            console.error("Login error:", error);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(firebaseAuthInstance);
            setOrders([]); // Clear data on logout
            // Removed setCustomers as the tab is removed
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    const fetchOrders = async () => {
        setLoading(true);
        try {
            // Using the backend API for orders
            const response = await fetch('/api/orders'); // Use relative path for Netlify proxy
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            // Map over the data to convert Firestore Timestamps to Date objects if necessary
            const processedOrders = data.map(order => {
                let createdAtDate = null;
                // Check if createdAt is an object with _seconds and _nanoseconds (Firestore Timestamp serialization)
                if (order.createdAt && typeof order.createdAt === 'object' &&
                    typeof order.createdAt._seconds === 'number' &&
                    typeof order.createdAt._nanoseconds === 'number') {
                    createdAtDate = new Date(order.createdAt._seconds * 1000 + order.createdAt._nanoseconds / 1000000);
                } else if (order.createdAt instanceof Date) {
                    createdAtDate = order.createdAt; // Already a Date object
                } else if (typeof order.createdAt === 'string') {
                    createdAtDate = new Date(order.createdAt); // Try parsing as string (e.g., ISO string)
                }

                return {
                    ...order,
                    // Replace the original createdAt with a proper Date object or null
                    createdAt: createdAtDate
                };
            });

            // Sort orders by createdAt in descending order (most recent first)
            const sortedOrders = processedOrders.sort((a, b) => {
                const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(0); // Ensure it's a Date object for comparison
                const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(0); // Ensure it's a Date object for comparison
                return dateB.getTime() - dateA.getTime(); // Sort descending
            });

            setOrders(sortedOrders);
        } catch (error) {
            console.error("Error fetching orders:", error);
            setMessage({ type: 'error', text: 'Failed to fetch orders.' });
        } finally {
            setLoading(false);
        }
    };

    // Removed fetchCustomers function
    // Removed handleSearchCustomers function

    // Modified openOrderModal to fetch full order details from Firestore
    const openOrderModal = async (order) => {
        if (!firestoreDbInstance) {
            setMessage({ type: 'error', text: 'Database not initialized. Cannot fetch order details.' });
            return;
        }
        setLoading(true);
        setMessage({ type: '', text: '' }); // Clear previous messages
        setRefundReason(''); // Clear refund reason when opening modal
        // Reset collapsible sections when opening modal
        setShowSenderDetails(false);
        setShowReceiverDetails(false);
        setShowPackageDetails(false);
        setShowOptionsDetails(false);
        setShowInternationalDetails(false);

        try {
            // Corrected Firestore path: orders are directly under the root
            const orderDocRef = doc(firestoreDbInstance, 'orders', order.id);
            const orderDocSnap = await getDoc(orderDocRef);

            if (orderDocSnap.exists()) {
                const fullOrderData = { id: orderDocSnap.id, ...orderDocSnap.data() };

                // Fetch totalCost from Stripe if it's missing or 0 in Firestore
                if (fullOrderData.paymentIntentId && (fullOrderData.totalCost === undefined || fullOrderData.totalCost === null || fullOrderData.totalCost === 0)) {
                    try {
                        const response = await fetch('/api/get-payment-intent', { // Assuming you have this backend endpoint
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ paymentIntentId: fullOrderData.paymentIntentId })
                        });
                        const data = await response.json();
                        if (response.ok && data.amount) {
                            fullOrderData.totalCost = data.amount / 100; // Convert cents to dollars
                        } else {
                            fullOrderData.totalCost = 'N/A (Stripe data missing)';
                        }
                    } catch (stripeError) {
                        console.error(`Error retrieving Stripe Payment Intent ${fullOrderData.paymentIntentId}:`, stripeError);
                        fullOrderData.totalCost = 'Error fetching cost (Stripe API)';
                    }
                }
                
                setSelectedOrder(fullOrderData);
                setShowOrderModal(true);
                // Log the totalCost and refundAmount to console for debugging
                console.log("[openOrderModal] Selected Order Total Cost:", fullOrderData.totalCost);
                console.log("[openOrderModal] Selected Order Refund Amount:", fullOrderData.refundAmount);
            } else {
                setMessage({ type: 'error', text: 'Order details not found in database.' });
                console.error(`Order document ${order.id} not found at path: orders/${order.id}`);
            }
        } catch (error) {
            console.error("Error fetching full order details:", error);
            setMessage({ type: 'error', text: `Failed to load order details: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

    const closeOrderModal = () => {
        setShowOrderModal(false);
        setSelectedOrder(null);
        setRefundReason(''); // Clear refund reason on close
        setMessage({ type: '', text: '' }); // Clear messages on close
        // Reset collapsible sections when closing modal
        setShowSenderDetails(false);
        setShowReceiverDetails(false);
        setShowPackageDetails(false);
        setShowOptionsDetails(false);
        setShowInternationalDetails(false);
    };

    // New function to handle issuing a refund, now accepts an order object
    const handleIssueRefund = async (orderToRefund) => {
        // Use orderToRefund if provided, otherwise fall back to selectedOrder (for modal context)
        const order = orderToRefund || selectedOrder;

        if (!order || !order.paymentIntentId || typeof order.totalCost === 'undefined' || order.totalCost === null) {
            setMessage({ type: 'error', text: 'Error: Missing payment details or total cost for refund.' });
            return;
        }

        // Convert totalCost to cents for Stripe API
        const amountInCents = Math.round(order.totalCost * 100);

        showCustomConfirmModal(`Are you sure you want to issue a refund of $${order.totalCost.toFixed(2)} for Order ID: ${order.id}?`, async () => {
            setLoading(true);
            setMessage({ type: '', text: '' }); // Clear previous messages

            try {
                setMessage({ type: 'info', text: 'Processing refund...' });
                const response = await fetch('/api/process-stripe-refund', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        paymentIntentId: order.paymentIntentId,
                        amount: amountInCents,
                        reason: refundReason, // Use refundReason from state
                        orderId: order.id, // Pass orderId to update Firestore in backend
                    }),
                });

                const result = await response.json();

                if (response.ok) {
                    setMessage({ type: 'success', text: `Refund processed successfully! Refund ID: ${result.refundId}` });
                    // Update the order status locally and in Firestore
                    // Log order.totalCost before updating Firestore for debugging
                    console.log("[handleIssueRefund] Order Total Cost being used for refundAmount:", order.totalCost);
                    const orderRef = doc(firestoreDbInstance, 'orders', order.id);
                    await updateDoc(orderRef, {
                        paymentStatus: 'refunded',
                        status: 'Refunded', // Set order status to Refunded
                        refundId: result.refundId,
                        refundAmount: order.totalCost, // Store the original totalCost as refundAmount
                        refundReason: refundReason,
                        updatedAt: new Date(),
                    });
                    fetchOrders(); // Re-fetch orders to update the list
                    // If the refund was initiated from the modal, close it
                    if (showOrderModal) {
                        closeOrderModal();
                    }
                } else {
                    setMessage({ type: 'error', text: `Refund failed: ${result.error || 'Unknown error'}` });
                }
            } catch (error) {
                console.error("Error issuing refund:", error);
                setMessage({ type: 'error', text: `An unexpected error occurred: ${error.message}` });
            } finally {
                setLoading(false);
                closeCustomConfirmModal(); // Close confirm modal after action completes
            }
        });
    };

    // Removed the unused handleUpdateOrder function
    // const handleUpdateOrder = async () => { /* ... */ };

    const handleDeleteOrder = async (orderId) => {
        showCustomConfirmModal('Are you sure you want to cancel this order? This will cancel it on Flagship and update its status in the database.', async () => {
            if (!firestoreDbInstance) return;
            setLoading(true);
            setMessage({ type: '', text: '' }); // Clear previous messages

            try {
                // Find the order to get its shipmentId
                const orderToCancel = orders.find(order => order.id === orderId);
                if (!orderToCancel || !orderToCancel.shipmentId) {
                    throw new Error('Order or Flagship Shipment ID not found for cancellation.');
                }

                // Call backend endpoint to delete/cancel shipment on Flagship
                const flagshipResponse = await fetch(`/api/shipments/${orderToCancel.shipmentId}`, {
                    method: 'DELETE',
                });

                // Flagship API returns 204 No Content for successful deletion, so response.json() might fail.
                // Check response.status first.
                if (flagshipResponse.status === 204) {
                    // If 204, no content to parse, just proceed
                    setMessage({ type: 'success', text: 'Shipment cancelled successfully on Flagship!' });
                } else if (!flagshipResponse.ok) {
                    // If not 204 and not OK (e.g., 4xx, 5xx), try to parse error data
                    const flagshipData = await flagshipResponse.json();
                    const errorMessage = flagshipData.error || (flagshipData.errors && JSON.stringify(flagshipData.errors)) || 'Unknown error from Flagship API.';
                    throw new Error(`Flagship API cancellation failed: ${errorMessage}`);
                } else {
                    // If Flagship returns 200 OK with content (unexpected for DELETE but possible),
                    // you might want to log it or handle it. For now, assume success.
                    setMessage({ type: 'success', text: 'Shipment cancelled successfully on Flagship!' });
                }


                // If Flagship cancellation is successful, update Firestore status to 'Cancelled'
                // Corrected Firestore path: orders are directly under the root
                await updateDoc(doc(firestoreDbInstance, 'orders', orderId), {
                    status: 'Cancelled', // Set status to 'Cancelled'
                    updatedAt: new Date(), // Use client-side timestamp for immediate UI update
                    flagshipCancellationMessage: 'Cancelled on Flagship' // Store a generic success message
                });

                setMessage({ type: 'success', text: 'Order cancelled successfully on Flagship and status updated in Firestore!' });
                fetchOrders(); // Refresh orders list
            } catch (error) {
                console.error("Error cancelling order:", error);
                setMessage({ type: 'error', text: `Failed to cancel order: ${error.message}` });
            } finally {
                setLoading(false);
                closeCustomConfirmModal(); // Close modal after action
            }
        });
    };

    // Function to handle viewing the shipping label
    const handleViewLabel = (order) => {
        if (order.labelUrl) {
            window.open(order.labelUrl, '_blank');
        } else {
            setMessage({ type: 'error', text: 'No shipping label URL available for this order.' });
        }
    };

    if (loading || !isFirebaseInitialized) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
                <p className="text-2xl font-semibold">Loading Admin Panel...</p>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
                    <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Admin Login</h2>
                    <form onSubmit={handleLogin}>
                        <div className="mb-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-6">
                            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                                Password
                            </label>
                            <input
                                type="password"
                                id="password"
                                className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 mb-3 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        {loginError && <p className="text-red-500 text-xs italic mb-4 text-center">{loginError}</p>}
                        <div className="flex items-center justify-center">
                            <button
                                type="submit"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition duration-300 ease-in-out shadow-md"
                            >
                                Sign In
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            {/* Header */}
            <header className="bg-gray-900 text-white shadow-lg p-4">
                <div className="container mx-auto flex justify-between items-center">
                    <h1 className="text-3xl font-bold rounded-md px-3 py-1 bg-white text-gray-900 shadow-md">Blue Lane Express Admin</h1>
                    <nav>
                        <ul className="flex space-x-6">
                            <li>
                                <button
                                    onClick={() => setActiveTab('orders')}
                                    className={`text-lg font-medium transition duration-300 ease-in-out hover:text-gray-300 ${activeTab === 'orders' ? 'border-b-2 border-blue-400 pb-1' : ''}`}
                                >
                                    Orders
                                </button>
                            </li>
                            {/* Removed Customers Tab Button */}
                            <li>
                                <button
                                    onClick={() => setActiveTab('history')}
                                    className={`text-lg font-medium transition duration-300 ease-in-out hover:text-gray-300 ${activeTab === 'history' ? 'border-b-2 border-blue-400 pb-1' : ''}`}
                                >
                                    Customer History
                                </button>
                            </li>
                        </ul>
                    </nav>
                    <div className="flex items-center space-x-4">
                        <span className="text-sm">Welcome, {user.email || 'Admin'}!</span>
                        <button
                            onClick={handleLogout}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-grow container mx-auto p-8">
                {message.text && (
                    <div className={`p-4 mb-4 rounded-lg text-white ${message.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
                        {message.text}
                    </div>
                )}

                {activeTab === 'orders' && (
                    <div className="bg-white p-8 rounded-lg shadow-xl border border-gray-200">
                        <h2 className="text-4xl font-extrabold text-gray-800 mb-6 text-center">Manage Orders</h2>
                        <button
                            onClick={fetchOrders}
                            className="mb-6 px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition duration-300 ease-in-out font-semibold"
                        >
                            Refresh Orders
                        </button>
                        {loading ? (
                            <p className="text-center text-gray-600">Loading orders...</p>
                        ) : orders.length === 0 ? (
                            <p className="text-center text-gray-600">No orders found.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                    <thead>
                                    <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm leading-normal">
                                        <th className="py-3 px-6">Order ID</th>
                                        <th className="py-3 px-6">Shipment ID</th>
                                        <th className="py-3 px-6">Sender</th>
                                        <th className="py-3 px-6">Receiver</th>
                                        <th className="py-3 px-6">Status</th>
                                        <th className="py-3 px-6">Total Cost</th>
                                        <th className="py-3 px-6">Created At</th>
                                        <th className="py-3 px-6">Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody className="text-gray-700 text-sm font-light">
                                    {orders.map((order) => (
                                        <tr key={order.id} className="border-b border-gray-200 hover:bg-gray-50">
                                            <td className="py-3 px-6 whitespace-nowrap">{order.id}</td>
                                            <td className="py-3 px-6">{order.shipmentId || 'N/A'}</td>
                                            <td className="py-3 px-6">{order.sender?.name || 'N/A'}</td>
                                            <td className="py-3 px-6">{order.receiver?.name || 'N/A'}</td>
                                            <td className="py-3 px-6">
                                                <span className={`py-1 px-3 rounded-full text-xs font-semibold ${
                                                    order.status === 'Pending' ? 'bg-yellow-200 text-yellow-800' :
                                                        order.status === 'In Transit' ? 'bg-blue-200 text-blue-800' :
                                                            order.status === 'Delivered' ? 'bg-green-200 text-green-800' :
                                                                order.status === 'Cancelled' || order.status === 'Refunded' ? 'bg-red-200 text-red-800' :
                                                                'bg-gray-200 text-gray-800'
                                                }`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-6">${typeof order.totalCost === 'number' ? order.totalCost.toFixed(2) : 'N/A'}</td>
                                            <td className="py-3 px-6">{order.createdAt instanceof Date ? order.createdAt.toLocaleString() : 'N/A'}</td>
                                            <td className="py-3 px-6 flex space-x-2">
                                                {/* Issue Refund button */}
                                                <button
                                                    onClick={() => handleIssueRefund(order)}
                                                    disabled={!order.paymentIntentId || order.paymentStatus === 'refunded' || order.status === 'Refunded'}
                                                    className={`py-1 px-3 rounded-md text-xs font-semibold transition duration-200 ${
                                                        (!order.paymentIntentId || order.paymentStatus === 'refunded' || order.status === 'Refunded')
                                                            ? 'bg-gray-400 cursor-not-allowed'
                                                            : 'bg-red-600 hover:bg-red-700 text-white'
                                                    }`}
                                                >
                                                    Issue Refund
                                                </button>
                                                {/* View Order Details button */}
                                                <button
                                                    onClick={() => openOrderModal(order)}
                                                    className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded-md text-xs transition duration-200"
                                                >
                                                    View Order
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteOrder(order.id)}
                                                    className="bg-red-500 hover:bg-red-600 text-white py-1 px-3 rounded-md text-xs transition duration-200"
                                                >
                                                    Delete
                                                </button>
                                                <button
                                                    onClick={() => handleViewLabel(order)}
                                                    disabled={!order.labelUrl}
                                                    className={`py-1 px-3 rounded-md text-xs font-semibold transition duration-200 ${
                                                        order.labelUrl
                                                            ? 'bg-green-500 hover:bg-green-600 text-white'
                                                            : 'bg-gray-400 cursor-not-allowed'
                                                    }`}
                                                >
                                                    View Label
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Removed Customers Tab Content */}

                {activeTab === 'history' && isFirebaseInitialized && firestoreDbInstance && firebaseAuthInstance && (
                    <CustomerHistoryPanel
                        db={firestoreDbInstance}
                        auth={firebaseAuthInstance}
                        openOrderModal={openOrderModal}
                        handleIssueRefund={handleIssueRefund}
                        handleDeleteOrder={handleDeleteOrder}
                        handleViewLabel={handleViewLabel}
                        fetchOrders={fetchOrders} // Pass fetchOrders to allow refreshing main list after actions
                    />
                )}
                {activeTab === 'history' && !isFirebaseInitialized && (
                    <div className="bg-white p-8 rounded-lg shadow-xl border border-gray-200 text-center text-gray-600">
                        Firebase is still initializing or failed to initialize. Please wait.
                    </div>
                )}
            </main>

            {/* Order Details Modal */}
            {showOrderModal && selectedOrder && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"> {/* Added max-h and overflow-y */}
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">Order Details: {selectedOrder.id}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <p className="text-gray-600 font-semibold">Order ID:</p>
                                <p className="text-gray-800">{selectedOrder.id}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 font-semibold">Customer Email:</p>
                                <p className="text-gray-800">{selectedOrder.customerEmail || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 font-semibold">Total Cost:</p>
                                <p className="text-gray-800">${typeof selectedOrder.totalCost === 'number' ? selectedOrder.totalCost.toFixed(2) : 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 font-semibold">Tracking Number:</p>
                                <p className="text-gray-800">{selectedOrder.trackingNumber || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 font-semibold">Shipment ID (Flagship):</p>
                                <p className="text-gray-800">{selectedOrder.shipmentId || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 font-semibold">Payment Intent ID:</p>
                                <p className="text-gray-800">{selectedOrder.paymentIntentId || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 font-semibold">Payment Status:</p>
                                <p className="text-gray-800">{selectedOrder.paymentStatus || 'N/A'}</p>
                            </div>
                            {selectedOrder.refundId && (
                                <div>
                                    <p className="text-gray-600 font-semibold">Refund ID:</p>
                                    <p className="text-gray-800">{selectedOrder.refundId}</p>
                                </div>
                            )}
                            {selectedOrder.refundAmount && (
                                <div>
                                    <p className="text-gray-600 font-semibold">Refund Amount:</p>
                                    <p className="text-gray-800">${typeof selectedOrder.refundAmount === 'number' ? selectedOrder.refundAmount.toFixed(2) : 'N/A'}</p>
                                </div>
                            )}
                            {selectedOrder.refundReason && (
                                <div>
                                    <p className="text-gray-600 font-semibold">Refund Reason:</p>
                                    <p className="text-gray-800">{selectedOrder.refundReason}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-gray-600 font-semibold">Order Status:</p>
                                <p className="text-gray-800">{selectedOrder.status}</p>
                            </div>
                            <div>
                                <p className="text-gray-600 font-semibold">Order Notes:</p>
                                <p className="text-gray-800">{selectedOrder.notes || 'N/A'}</p>
                            </div>
                             <div>
                                <p className="text-gray-600 font-semibold">Delivery Type:</p>
                                <p className="text-gray-800">{selectedOrder.deliveryType || 'N/A'}</p>
                            </div>
                             <div>
                                <p className="text-gray-600 font-semibold">Created At:</p>
                                <p className="text-gray-800">{selectedOrder.createdAt instanceof Date ? selectedOrder.createdAt.toLocaleString() : 'N/A'}</p>
                            </div>
                            {selectedOrder.updatedAt && (
                                <div>
                                    <p className="text-gray-600 font-semibold">Last Updated:</p>
                                    <p className="text-gray-800">{selectedOrder.updatedAt.toDate ? selectedOrder.updatedAt.toDate().toLocaleString() : (selectedOrder.updatedAt instanceof Date ? selectedOrder.updatedAt.toLocaleString() : 'N/A')}</p>
                                </div>
                            )}
                        </div>

                        {/* Sender Details Section */}
                        <div className="mb-4 p-4 border border-gray-200 rounded-lg">
                            <button
                                className="w-full text-left font-bold text-lg text-gray-800 flex justify-between items-center"
                                onClick={() => setShowSenderDetails(!showSenderDetails)}
                            >
                                Sender Details
                                <span>{showSenderDetails ? '▲' : '▼'}</span>
                            </button>
                            {showSenderDetails && selectedOrder.sender && (
                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                    <p><span className="font-semibold">Name:</span> {selectedOrder.sender.name || 'N/A'}</p>
                                    <p><span className="font-semibold">Email:</span> {selectedOrder.customerEmail || 'N/A'}</p> {/* Assuming customerEmail is sender's email */}
                                    <p><span className="font-semibold">Address:</span> {selectedOrder.sender.address || 'N/A'}</p>
                                    <p><span className="font-semibold">Suite/Apt:</span> {selectedOrder.sender.suite || 'N/A'}</p>
                                    <p><span className="font-semibold">City:</span> {selectedOrder.sender.city || 'N/A'}</p>
                                    <p><span className="font-semibold">Province/State:</span> {selectedOrder.sender.province || 'N/A'}</p>
                                    <p><span className="font-semibold">Postal Code:</span> {selectedOrder.sender.postal_code || 'N/A'}</p>
                                    <p><span className="font-semibold">Country:</span> {selectedOrder.sender.country || 'N/A'}</p>
                                    <p><span className="font-semibold">Phone:</span> {selectedOrder.sender.phone || 'N/A'}</p>
                                    <p><span className="font-semibold">Ext:</span> {selectedOrder.sender.ext || 'N/A'}</p>
                                    <p><span className="font-semibold">Attention:</span> {selectedOrder.sender.attn || 'N/A'}</p>
                                    <p><span className="font-semibold">Department:</span> {selectedOrder.sender.department || 'N/A'}</p>
                                    <p><span className="font-semibold">Commercial:</span> {selectedOrder.sender.is_commercial ? 'Yes' : 'No'}</p>
                                </div>
                            )}
                        </div>

                        {/* Receiver Details Section */}
                        <div className="mb-4 p-4 border border-gray-200 rounded-lg">
                            <button
                                className="w-full text-left font-bold text-lg text-gray-800 flex justify-between items-center"
                                onClick={() => setShowReceiverDetails(!showReceiverDetails)}
                            >
                                Receiver Details
                                <span>{showReceiverDetails ? '▲' : '▼'}</span>
                            </button>
                            {showReceiverDetails && selectedOrder.receiver && (
                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                    <p><span className="font-semibold">Name:</span> {selectedOrder.receiver.name || 'N/A'}</p>
                                    <p><span className="font-semibold">Address:</span> {selectedOrder.receiver.address || 'N/A'}</p>
                                    <p><span className="font-semibold">Suite/Apt:</span> {selectedOrder.receiver.suite || 'N/A'}</p>
                                    <p><span className="font-semibold">City:</span> {selectedOrder.receiver.city || 'N/A'}</p>
                                    <p><span className="font-semibold">Province/State:</span> {selectedOrder.receiver.province || 'N/A'}</p>
                                    <p><span className="font-semibold">Postal Code:</span> {selectedOrder.receiver.postal_code || 'N/A'}</p>
                                    <p><span className="font-semibold">Country:</span> {selectedOrder.receiver.country || 'N/A'}</p>
                                    <p><span className="font-semibold">Phone:</span> {selectedOrder.receiver.phone || 'N/A'}</p>
                                    <p><span className="font-semibold">Ext:</span> {selectedOrder.receiver.ext || 'N/A'}</p>
                                    <p><span className="font-semibold">Attention:</span> {selectedOrder.receiver.attn || 'N/A'}</p>
                                    <p><span className="font-semibold">Department:</span> {selectedOrder.receiver.department || 'N/A'}</p>
                                    <p><span className="font-semibold">Commercial:</span> {selectedOrder.receiver.is_commercial ? 'Yes' : 'No'}</p>
                                </div>
                            )}
                        </div>

                        {/* Package Details Section */}
                        <div className="mb-4 p-4 border border-gray-200 rounded-lg">
                            <button
                                className="w-full text-left font-bold text-lg text-gray-800 flex justify-between items-center"
                                onClick={() => setShowPackageDetails(!showPackageDetails)}
                            >
                                Package Details
                                <span>{showPackageDetails ? '▲' : '▼'}</span>
                            </button>
                            {showPackageDetails && selectedOrder.package && (
                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                    <p><span className="font-semibold">Description:</span> {selectedOrder.package.description || 'N/A'}</p>
                                    <p><span className="font-semibold">Weight:</span> {selectedOrder.package.weight || 'N/A'} {selectedOrder.package.units === 'imperial' ? 'lbs' : 'kg'}</p>
                                    <p><span className="font-semibold">Dimensions (LxWxH):</span> {selectedOrder.package.dimensions?.length || 'N/A'}x{selectedOrder.package.dimensions?.width || 'N/A'}x{selectedOrder.package.dimensions?.height || 'N/A'} {selectedOrder.package.units === 'imperial' ? 'in' : 'cm'}</p>
                                    <p><span className="font-semibold">Units:</span> {selectedOrder.package.units || 'N/A'}</p>
                                    <p><span className="font-semibold">Type:</span> {selectedOrder.package.type || 'N/A'}</p>
                                    <p><span className="font-semibold">Content:</span> {selectedOrder.package.content || 'N/A'}</p>
                                </div>
                            )}
                        </div>

                        {/* Options Details Section */}
                        <div className="mb-4 p-4 border border-gray-200 rounded-lg">
                            <button
                                className="w-full text-left font-bold text-lg text-gray-800 flex justify-between items-center"
                                onClick={() => setShowOptionsDetails(!showOptionsDetails)}
                            >
                                Shipping Options
                                <span>{showOptionsDetails ? '▲' : '▼'}</span>
                            </button>
                            {showOptionsDetails && selectedOrder.options && (
                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                    <p><span className="font-semibold">Signature Required:</span> {selectedOrder.options.signature_required ? 'Yes' : 'No'}</p>
                                    <p><span className="font-semibold">Saturday Delivery:</span> {selectedOrder.options.saturday_delivery ? 'Yes' : 'No'}</p>
                                    <p><span className="font-semibold">Shipping Date:</span> {selectedOrder.options.shipping_date || 'N/A'}</p>
                                    <p><span className="font-semibold">Return Documents As:</span> {selectedOrder.options.return_documents_as || 'N/A'}</p>
                                    <p><span className="font-semibold">Reference:</span> {selectedOrder.options.reference || 'N/A'}</p>
                                    <p><span className="font-semibold">Driver Instructions:</span> {selectedOrder.options.driver_instructions || 'N/A'}</p>
                                    <p><span className="font-semibold">Address Correction:</span> {selectedOrder.options.address_correction ? 'Yes' : 'No'}</p>
                                    <p><span className="font-semibold">Tracking Emails:</span> {selectedOrder.options.shipment_tracking_emails?.join(', ') || 'N/A'}</p>
                                    {selectedOrder.options.insurance && (
                                        <>
                                            <p><span className="font-semibold">Insurance Value:</span> ${selectedOrder.options.insurance.value ? selectedOrder.options.insurance.value.toFixed(2) : 'N/A'}</p>
                                            <p><span className="font-semibold">Insurance Description:</span> {selectedOrder.options.insurance.description || 'N/A'}</p>
                                        </>
                                    )}
                                    {selectedOrder.options.cod && (
                                        <>
                                            <p><span className="font-semibold">COD Method:</span> {selectedOrder.options.cod.method || 'N/A'}</p>
                                            <p><span className="font-semibold">COD Payable To:</span> {selectedOrder.options.cod.payable_to || 'N/A'}</p>
                                            <p><span className="font-semibold">COD Receiver Phone:</span> {selectedOrder.options.cod.receiver_phone || 'N/A'}</p>
                                            <p><span className="font-semibold">COD Amount:</span> ${selectedOrder.options.cod.amount ? selectedOrder.options.cod.amount.toFixed(2) : 'N/A'}</p>
                                            <p><span className="font-semibold">COD Currency:</span> {selectedOrder.options.cod.currency || 'N/A'}</p>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* International Details Section (Conditionally Rendered) */}
                        {(selectedOrder.sold_to || selectedOrder.inquiry || (selectedOrder.declared_items && selectedOrder.declared_items.length > 0)) && (
                            <div className="mb-4 p-4 border border-gray-200 rounded-lg">
                                <button
                                    className="w-full text-left font-bold text-lg text-gray-800 flex justify-between items-center"
                                    onClick={() => setShowInternationalDetails(!showInternationalDetails)}
                                >
                                    International Details
                                    <span>{showInternationalDetails ? '▲' : '▼'}</span>
                                </button>
                                {showInternationalDetails && (
                                    <div className="mt-4 space-y-4">
                                        {selectedOrder.sold_to && (
                                            <div>
                                                <h4 className="font-semibold text-md text-gray-700 mb-2">Sold To:</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                                    <p><span className="font-semibold">Duties Payer:</span> {selectedOrder.sold_to.duties_payer || 'N/A'}</p>
                                                    <p><span className="font-semibold">Duties Payer Account:</span> {selectedOrder.sold_to.duties_payer_account || 'N/A'}</p>
                                                    <p><span className="font-semibold">Reason for Export:</span> {selectedOrder.sold_to.reason_for_export || 'N/A'}</p>
                                                    <p><span className="font-semibold">Is NAFTA:</span> {selectedOrder.sold_to.is_nafta ? 'Yes' : 'No'}</p>
                                                    {selectedOrder.sold_to.sold_to_address && (
                                                        <>
                                                            <p><span className="font-semibold">Name:</span> {selectedOrder.sold_to.sold_to_address.name || 'N/A'}</p>
                                                            <p><span className="font-semibold">Address:</span> {selectedOrder.sold_to.sold_to_address.address || 'N/A'}</p>
                                                            <p><span className="font-semibold">City:</span> {selectedOrder.sold_to.sold_to_address.city || 'N/A'}</p>
                                                            <p><span className="font-semibold">State:</span> {selectedOrder.sold_to.sold_to_address.state || 'N/A'}</p>
                                                            <p><span className="font-semibold">Postal Code:</span> {selectedOrder.sold_to.sold_to_address.postal_code || 'N/A'}</p>
                                                            <p><span className="font-semibold">Country:</span> {selectedOrder.sold_to.sold_to_address.country || 'N/A'}</p>
                                                            <p><span className="font-semibold">Phone:</span> {selectedOrder.sold_to.sold_to_address.phone || 'N/A'}</p>
                                                            <p><span className="font-semibold">Commercial:</span> {selectedOrder.sold_to.sold_to_address.is_commercial ? 'Yes' : 'No'}</p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {selectedOrder.inquiry && (
                                            <div>
                                                <h4 className="font-semibold text-md text-gray-700 mb-2">Inquiry:</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                                                    <p><span className="font-semibold">Company:</span> {selectedOrder.inquiry.company || 'N/A'}</p>
                                                    <p><span className="font-semibold">Name:</span> {selectedOrder.inquiry.name || 'N/A'}</p>
                                                    <p><span className="font-semibold">Broker ID:</span> {selectedOrder.inquiry.broker_id || 'N/A'}</p>
                                                    <p><span className="font-semibold">Sender Tax ID:</span> {selectedOrder.inquiry.sender_tax_id || 'N/A'}</p>
                                                    <p><span className="font-semibold">Receiver Tax ID:</span> {selectedOrder.inquiry.receiver_tax_id || 'N/A'}</p>
                                                    <p><span className="font-semibold">Phone:</span> {selectedOrder.inquiry.inquiry_phone || 'N/A'}</p>
                                                </div>
                                            </div>
                                        )}

                                        {selectedOrder.declared_items && selectedOrder.declared_items.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-md text-gray-700 mb-2">Declared Items:</h4>
                                                <div className="space-y-2">
                                                    {selectedOrder.declared_items.map((item, index) => (
                                                        <div key={index} className="border-t border-gray-100 pt-2">
                                                            <p><span className="font-semibold">Product Name:</span> {item.product_name || 'N/A'}</p>
                                                            <p><span className="font-semibold">Description:</span> {item.description || 'N/A'}</p>
                                                            <p><span className="font-semibold">Country of Origin:</span> {item.country_of_origin || 'N/A'}</p>
                                                            <p><span className="font-semibold">Quantity:</span> {item.quantity || 'N/A'}</p>
                                                            <p><span className="font-semibold">Unit Price:</span> ${item.unit_price ? item.unit_price.toFixed(2) : 'N/A'}</p>
                                                            <p><span className="font-semibold">Unit Weight:</span> {item.unit_weight || 'N/A'} {item.unit_of_measurement || 'N/A'}</p>
                                                            <p><span className="font-semibold">HS Code:</span> {item.HS_code || 'N/A'}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Refund Reason Input (only if not already refunded) */}
                        {selectedOrder.paymentIntentId && selectedOrder.paymentStatus !== 'refunded' && selectedOrder.status !== 'Refunded' && (
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="refundReason">
                                    Refund Reason (Optional)
                                </label>
                                <textarea
                                    id="refundReason"
                                    className="shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    rows="2"
                                    value={refundReason}
                                    onChange={(e) => setRefundReason(e.target.value)}
                                    placeholder="Enter reason for refund..."
                                ></textarea>
                            </div>
                        )}

                        {message.text && (
                            <div className={`p-3 mb-4 rounded-lg text-white ${message.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
                                {message.text}
                            </div>
                        )}

                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={closeOrderModal}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
                            >
                                Close
                            </button>
                            {/* Issue Refund button inside the modal */}
                            {selectedOrder.paymentIntentId && selectedOrder.paymentStatus !== 'refunded' && selectedOrder.status !== 'Refunded' && (
                                <button
                                    onClick={() => handleIssueRefund(selectedOrder)} // Pass selectedOrder explicitly
                                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
                                >
                                    Issue Refund
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-sm">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Confirm Action</h3>
                        <p className="text-gray-700 mb-6">{confirmModalContent}</p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={closeCustomConfirmModal}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirm}
                                className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer className="bg-gray-800 text-white p-6 text-center shadow-inner mt-8">
                <p>&copy; {new Date().getFullYear()} Blue Lane Express Admin. All rights reserved.</p>
            </footer>
        </div>
    );
}

export default App;
